import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { serverErrorMessage } from '@/shared/api'
import { message, useRoutePathParam } from '@/shared/lib'

import {
  useSubscriptionStatus,
  useUnsubscribe
} from '@/apps/adok/entities/subscription'

const INVALID_LINK = 'Invalid unsubscribe link'

export function useUnsubscribePage() {
  const router = useRouter()

  const token = useRoutePathParam('token')

  const status = useSubscriptionStatus(() => token.value ?? '')
  const unsubscribing = useUnsubscribe()

  /**
   * Set the moment the mutation succeeds, and never unset. The re-read that follows turns
   * `unsubscribed` true as well, and without this flag the screen could not tell the two
   * apart — it would greet someone who just pressed the button with "you already did".
   */
  const justUnsubscribed = ref(false)

  const isLoading = computed(() => status.isLoading.value)

  const alreadyUnsubscribed = computed(() => status.unsubscribed.value === true)

  const canUnsubscribe = computed(
    () => status.unsubscribed.value === false && !justUnsubscribed.value
  )

  const error = computed(() =>
    status.isError.value
      ? serverErrorMessage(status.error.value, INVALID_LINK)
      : ''
  )

  async function confirm() {
    try {
      await unsubscribing.mutateAsync(token.value ?? '')
      justUnsubscribed.value = true
      message.success('You have successfully unsubscribed.')
    } catch (err) {
      message.error(serverErrorMessage(err, INVALID_LINK))
    }
  }

  function goHome() {
    router.push({ name: 'dashboard' })
  }

  return {
    isLoading,
    error,
    alreadyUnsubscribed,
    justUnsubscribed,
    canUnsubscribe,
    isSubmitting: unsubscribing.isPending,
    confirm,
    goHome
  }
}
